import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { hitApi, commonToast } from "../../components/commonAction";
import { useStore } from "../../zustand/store";
import appConfig from "../../config/appConfig";
import "../affiliate/affiliate.css";

export default function TierCheckout({ pageTitle }) {
    const { code } = useParams();
    const navigate = useNavigate();
    const role = useStore(s => s.userData?.role);

    useEffect(() => {
        if (role === 'admin') navigate('/dashboard', { replace: true });
    }, [role, navigate]);
    const [tier, setTier] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        hitApi({ url: "billing/tiers", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            const found = (resp?.data?.tiers || []).find(t => t.code === code);
            setTier(found || null);
        });
    }, [code]);

    const buy = () => {
        if (!tier) return;
        setSubmitting(true);
        hitApi(
            {
                url: "billing/checkout/tier",
                method: "POST",
                data: {
                    tierId: tier._id,
                    affiliateRef: readPxlRefFromStorage(),
                },
                alert: false,
            },
            (resp) => {
                setSubmitting(false);
                if (resp?.status && resp.data?.approvalUrl) {
                    window.location.href = resp.data.approvalUrl;
                } else if (resp?.status) {
                    commonToast("error", "PayPal didn't return an approval URL. Try again.");
                }
            }
        );
    };

    function readPxlRefFromStorage() {
        try {
            const raw = localStorage.getItem('pxl_ref');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed?.code || !parsed?.expiresAt || parsed.expiresAt < Date.now()) {
                localStorage.removeItem('pxl_ref');
                return null;
            }
            return parsed.code;
        } catch (_) { return null; }
    }

    const isRecurring = tier?.billingType === "recurring";
    const billingLabel = isRecurring
        ? `Billed ${tier.billingInterval}ly until you cancel`
        : "One-time payment";
    const priceFmt = tier ? `${tier.currency} ${Number(tier.price).toFixed(2)}` : "";

    return (
        <div className="pxl-al-shell pxl-co-shell">
            <Helmet><title>{pageTitle}</title></Helmet>

            {/* ── Sticky header ── */}
            <header className="pxl-al-header">
                <div className="pxl-al-header-inner">
                    <Link to="/"><img src={appConfig.appLogo} alt={appConfig.appName} className="pxl-al-header-logo" /></Link>
                    <nav className="pxl-al-header-nav">
                        <Link to="/pricing" className="pxl-al-nav-link">All plans</Link>
                        <span className="pxl-co-secure-pill">
                            <IconLock /> Secure checkout
                        </span>
                    </nav>
                </div>
            </header>

            <div className="pxl-co-page">
                <div className="pxl-al-wrap pxl-co-wrap">
                    {loading ? (
                        <div className="pxl-co-state">Loading checkout…</div>
                    ) : !tier ? (
                        <div className="pxl-co-not-found">
                            <div className="pxl-co-not-found-icon" aria-hidden="true">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                            </div>
                            <h2>Plan not found</h2>
                            <p>We couldn't find a plan with the code <code>{code}</code>. It may have been renamed or is no longer available.</p>
                            <Link to="/pricing" className="pxlCommon-btn">Browse all plans</Link>
                        </div>
                    ) : (
                        <>
                            {/* Breadcrumb */}
                            <Link to="/pricing" className="pxl-co-back">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="11 18 5 12 11 6"/>
                                </svg>
                                Back to all plans
                            </Link>

                            <div className="pxl-co-page-head">
                                <h1 className="pxl-co-page-title">Complete your order</h1>
                                <p className="pxl-co-page-sub">
                                    You're one click away from getting started with <strong>{tier.name}</strong>.
                                </p>
                            </div>

                            <div className="pxl-co-grid">
                                {/* ── Left: Plan details ── */}
                                <div className="pxl-co-col-main">
                                    <div className="pxl-co-card">
                                        <div className="pxl-co-card-head">
                                            <div>
                                                <div className="pxl-co-eyebrow">Selected plan</div>
                                                <h2 className="pxl-co-plan-name">{tier.name}</h2>
                                                {tier.description && (
                                                    <p className="pxl-co-plan-desc">{tier.description}</p>
                                                )}
                                            </div>
                                            <div className="pxl-co-plan-icon" aria-hidden="true">
                                                <IconBox />
                                            </div>
                                        </div>

                                        <div className="pxl-co-divider" />

                                        <div className="pxl-co-includes-label">What's included</div>
                                        <ul className="pxl-co-includes">
                                            {(tier.plans || []).length === 0 ? (
                                                <li className="pxl-co-includes-empty">
                                                    Product bundles for this plan are being finalised. You'll get full access as soon as they're live.
                                                </li>
                                            ) : (
                                                (tier.plans || []).map((b, i) => (
                                                    <li key={i} className="pxl-co-includes-item">
                                                        <span className="pxl-co-includes-check"><IconCheck /></span>
                                                        <span className="pxl-co-includes-text">
                                                            <strong>{b.product?.name}</strong>
                                                            {b.plan?.name && <span className="pxl-co-includes-plan"> — {b.plan.name}</span>}
                                                        </span>
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    </div>

                                    {/* Trust panel */}
                                    <div className="pxl-co-trust-grid">
                                        <TrustChip icon={<IconShield />} title="14-day guarantee" desc="Money back if it's not the right fit." />
                                        <TrustChip icon={<IconLock />}    title="Secure payments"  desc="PayPal-protected. We never see your card." />
                                        <TrustChip icon={<IconRefresh />} title="Cancel anytime"   desc="One click in your dashboard." />
                                    </div>
                                </div>

                                {/* ── Right: Order summary (sticky) ── */}
                                <aside className="pxl-co-col-side">
                                    <div className="pxl-co-summary">
                                        <div className="pxl-co-summary-head">Order summary</div>

                                        <div className="pxl-co-summary-row">
                                            <span className="pxl-co-summary-label">{tier.name} plan</span>
                                            <span className="pxl-co-summary-value">{priceFmt}</span>
                                        </div>
                                        <div className="pxl-co-summary-row pxl-co-summary-row--muted">
                                            <span className="pxl-co-summary-label">Billing</span>
                                            <span className="pxl-co-summary-value">
                                                {isRecurring ? `Every ${tier.billingInterval}` : "One-time"}
                                            </span>
                                        </div>

                                        <div className="pxl-co-summary-divider" />

                                        <div className="pxl-co-summary-total">
                                            <span className="pxl-co-summary-total-label">
                                                {isRecurring ? "Total today" : "Total"}
                                            </span>
                                            <span className="pxl-co-summary-total-value">
                                                <span className="pxl-co-summary-total-cur">{tier.currency}</span>
                                                {Number(tier.price).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="pxl-co-summary-billing-note">{billingLabel}</div>

                                        <button
                                            className="pxlCommon-btn pxl-co-pay-btn"
                                            onClick={buy}
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <>Redirecting to PayPal…</>
                                            ) : (
                                                <>
                                                    <IconLock /> Pay {priceFmt}
                                                </>
                                            )}
                                        </button>

                                        <button type="button" className="pxl-co-cancel-link" onClick={() => navigate("/pricing")}>
                                            Cancel and choose a different plan
                                        </button>

                                        <div className="pxl-co-payment-rail">
                                            <div className="pxl-co-payment-label">Powered by</div>
                                            <div className="pxl-co-payment-paypal">PayPal</div>
                                        </div>

                                        <div className="pxl-co-fineprint">
                                            By completing your purchase you agree to our{" "}
                                            <Link to="/terms-conditions">Terms</Link> &amp;{" "}
                                            <Link to="/privacy-policy">Privacy Policy</Link>.
                                            {isRecurring && " You can cancel anytime from your dashboard."}
                                        </div>
                                    </div>
                                </aside>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ── Sub-components ────────────────────────────────────────────────────────── */
function TrustChip({ icon, title, desc }) {
    return (
        <div className="pxl-co-trust-chip">
            <div className="pxl-co-trust-chip-icon">{icon}</div>
            <div className="pxl-co-trust-chip-body">
                <div className="pxl-co-trust-chip-title">{title}</div>
                <div className="pxl-co-trust-chip-desc">{desc}</div>
            </div>
        </div>
    );
}

/* ── Icons ─────────────────────────────────────────────────────────────────── */
function IconCheck() {
    return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}
function IconLock() {
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
}
function IconShield() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
}
function IconRefresh() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
}
function IconBox() {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
}
